import {
  seriesCacheKey,
  questionsCacheKey,
  resultCacheKey,
  questionSessionsCacheKey,
  reactionsResultsCacheKey,
  quizPlayersScoreCacheKey,
  ratingGridsCacheKey,
  ratingGridCacheKey,
} from '@mentimeter/core-hooks';

interface CacheKeyArgs {
  quizPlayersScore: Parameters<typeof getQuizPlayersScoreCacheKey>[0];
  ratingGrid: Parameters<typeof getRatingGridCacheKey>[0];
  ratingGrids: never;
  reactionResults: Parameters<typeof getReactionResultsCacheKey>[0];
  results: Parameters<typeof getResultsCacheKey>[0];
  slideDeck: Parameters<typeof getSlideDeckCacheKey>[0];
  slides: Parameters<typeof getSlidesCacheKey>[0];
  slideSessions: Parameters<typeof getSlideSessionsCacheKey>[0];
}

type CacheKey = keyof CacheKeyArgs;

export function getEditorSWRCacheKey<Key extends CacheKey>(
  key: Key,
  args: CacheKeyArgs[Key],
): string {
  switch (key) {
    case 'quizPlayersScore': {
      return getQuizPlayersScoreCacheKey(
        args as CacheKeyArgs['quizPlayersScore'],
      );
    }
    case 'ratingGrid': {
      return getRatingGridCacheKey(args as CacheKeyArgs['ratingGrid']);
    }
    case 'ratingGrids': {
      return getRatingGridsCacheKey();
    }
    case 'reactionResults': {
      return getReactionResultsCacheKey(
        args as CacheKeyArgs['reactionResults'],
      );
    }
    case 'results': {
      return getResultsCacheKey(args as CacheKeyArgs['results']);
    }
    case 'slideDeck': {
      return getSlideDeckCacheKey(args as CacheKeyArgs['slideDeck']);
    }
    case 'slides': {
      return getSlidesCacheKey(args as CacheKeyArgs['slides']);
    }
    case 'slideSessions': {
      return getSlideSessionsCacheKey(args as CacheKeyArgs['slideSessions']);
    }
    default: {
      throw new Error(`Invalid cache key: ${key}`);
    }
  }
}

const DOMAIN = 'editor';
const SLIDE_DECK = 'slide-deck';
const SLIDE = 'slide';
const RATING_GRIDS = 'rating-grids';

interface SlideDeckCacheKeyArgs {
  slideDeckPublicKey: string;
}

type SlideDeckCacheKeyFn = ({
  slideDeckPublicKey,
}: SlideDeckCacheKeyArgs) => string;

interface SlideCacheKeyArgs {
  slideAdminKey: string;
}

type SlideCacheKeyFn = (args: SlideCacheKeyArgs) => string;

const getSlideDeckCacheKey: SlideDeckCacheKeyFn = ({ slideDeckPublicKey }) => {
  // TODO: Remove the `core-hooks` cache key.
  return seriesCacheKey(slideDeckPublicKey);
  return [DOMAIN, SLIDE_DECK, slideDeckPublicKey].join('/');
};

const getSlidesCacheKey: SlideDeckCacheKeyFn = ({ slideDeckPublicKey }) => {
  // TODO: Remove the `core-hooks` cache key.
  return questionsCacheKey(slideDeckPublicKey);
  return [DOMAIN, SLIDE_DECK, slideDeckPublicKey, 'slides'].join('/');
};

interface ResultsKeyArgs extends SlideCacheKeyArgs {
  session?: number | 'current';
}

type ResultsCacheKeyFn = (args: ResultsKeyArgs) => string;

const getResultsCacheKey: ResultsCacheKeyFn = ({
  slideAdminKey,
  session = 'current',
}) => {
  // TODO: Remove the `core-hooks` cache key.
  return resultCacheKey(slideAdminKey, session);
  return [DOMAIN, SLIDE, slideAdminKey, 'results'].join('/');
};

const getReactionResultsCacheKey: SlideCacheKeyFn = ({ slideAdminKey }) => {
  // TODO: Remove the `core-hooks` cache key.
  return reactionsResultsCacheKey(slideAdminKey);
  return [DOMAIN, SLIDE, slideAdminKey, 'reaction-results'].join('/');
};

const getSlideSessionsCacheKey: SlideCacheKeyFn = ({ slideAdminKey }) => {
  // TODO: Remove the `core-hooks` cache key.
  return questionSessionsCacheKey(slideAdminKey);
  return [DOMAIN, SLIDE, slideAdminKey, 'sessions'].join('/');
};

const getQuizPlayersScoreCacheKey: SlideCacheKeyFn = ({ slideAdminKey }) => {
  // TODO: Remove the `core-hooks` cache key.
  return quizPlayersScoreCacheKey(slideAdminKey);
  return [DOMAIN, SLIDE, slideAdminKey, 'quiz-players'].join('/');
};

const getRatingGridsCacheKey = (): string => {
  // TODO: Remove the `core-hooks` cache key.
  return ratingGridsCacheKey();
  return [DOMAIN, SLIDE, RATING_GRIDS].join('/');
};

const getRatingGridCacheKey = ({ id }: { id: string }): string => {
  // TODO: Remove the `core-hooks` cache key.
  return ratingGridCacheKey(id);
  return [DOMAIN, SLIDE, RATING_GRIDS, id].join('/');
};
